import { Description, Label, OutsideClick } from '@arionbanki/arionui';
import { classNames as cn } from 'helpers';
import isNil from 'lodash/isNil';
import { useState } from 'react';

type SharedProps = {
	error?: boolean;
	items: JSX.Element[];
	label: string;
	message?: string;
	onSelect?: (index: number) => void;
};

type SingleProps = {
	defaultValue?: number;
	multiselect?: false;
};

type Multiprops = {
	defaultValue?: number[];
	multiselect?: true;
};

export type DropdownProps = Readonly<SharedProps & (SingleProps | Multiprops)>;

type MenuEvent = React.MouseEvent<HTMLButtonElement, MouseEvent> | React.KeyboardEvent<HTMLButtonElement>;

const Message = Description as React.FC<{ active: boolean; error?: boolean; children?: React.ReactNode }>;

export function Dropdown(props: DropdownProps) {
	const { items, label, onSelect } = props;
	const selected = SelectedValue(props);
	const [selection, setSelection] = useState(new Set(selected));
	const [isOpen, setIsOpen] = useState(false);

	const outsideClick = () => setIsOpen(false);
	const toggleIsOpen = () => setIsOpen(!isOpen);

	const toggleSelected = (e: MenuEvent) => {
		e.stopPropagation();
		const index = Number((e.target as HTMLLIElement).dataset.index);
		setSelection(new Set([index]));
		setIsOpen(false);
		if (onSelect) onSelect(index);
	};

	const listCN = cn('arionui-dropdown--list--wrapper', {
		'arionui-dropdown--list--wrapper--open': isOpen
	});

	return (
		<OutsideClick handleClickOutside={outsideClick}>
			<div className="arionui-dropdown--wrapper">
				<div className={listCN}>
					<Label displayTop={isOpen || !!selection.size} text={label} />
					<div className="arionui-dropdown-outline--wrapper">
						<button className="arionui-dropdown--placeholder" onClick={toggleIsOpen} type="button">
							{Array.from(selection).map(i => items[i])}
						</button>
					</div>
					<ul className="arionui-dropdown--list">
						{items.map((item, index) => {
							const liCN = cn({ 'arionui-dropdown-li--selected': selection.has(index) });
							return (
								<li className={liCN} key={item.key}>
									<button className="list-item" data-index={index} onClick={toggleSelected} type="button">
										{item}
									</button>
								</li>
							);
						})}
					</ul>
				</div>
				<Message active={isOpen} error={props.error}>
					{props.message}
				</Message>
			</div>
		</OutsideClick>
	);
}

function SelectedValue({ defaultValue }: DropdownProps) {
	if (isNil(defaultValue)) return [];
	return Array.isArray(defaultValue) ? defaultValue : [defaultValue];
}
